import { DatePipe } from '@angular/common'
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { BaseComponent, FeatureType, SwpppActionItem, SwpppAnswer, SwpppDocument, SwpppInspectionDetail, SwpppQuestion } from '../../shared'

interface QuestionGroup {
    key: string
    title: string
    questions: SwpppQuestion[]
}

@Component({
    selector: 'app-swppp-inspection',
    templateUrl: './swppp-inspection.component.html',
    standalone: true,
    imports: [DatePipe, FormsModule, RouterLink],
})
export class SwpppInspectionComponent extends BaseComponent implements OnInit {
    @ViewChild('actionBox') actionBox?: ElementRef<HTMLTextAreaElement>

    detail?: SwpppInspectionDetail
    groups: QuestionGroup[] = []
    answers = new Map<string, SwpppAnswer>()
    signedName = ''
    signerTitle = ''
    confirmed = false
    actionText = ''
    visualNote = ''
    showDisclaimer = false
    message = ''

    constructor(
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super()
    }

    ngOnInit() {
        this.route.paramMap.subscribe(params => {
            const id = params.get('id')
            if (id) this.load(id)
        })
        this.signedName = this.services.authentication.displayName() || ''
    }

    load(id: string) {
        this.services.data.get<SwpppInspectionDetail>('/api/SwpppInspection/GetDetail?id=' + id).subscribe({
            next: detail => this.apply(detail),
            error: () => this.initialLoadingComplete(),
        })
    }

    apply(detail: SwpppInspectionDetail) {
        this.detail = detail
        this.groups = []
        for (const question of detail.questions ?? []) {
            let group = this.groups.find(item => item.key === question.groupKey)
            if (!group) {
                group = { key: question.groupKey, title: question.groupTitle || question.groupKey, questions: [] }
                this.groups.push(group)
            }
            group.questions.push(question)
            const existing = (detail.answers ?? []).find(answer => answer.questionId === question.id)
            this.answers.set(question.id, existing ?? { id: '', inspectionId: detail.inspection.id, questionId: question.id, value: '', otherText: '' })
        }
        this.signerTitle = detail.suggestedTitle || this.signerTitle
        this.initialLoadingComplete()
    }

    answer(question: SwpppQuestion) {
        return this.answers.get(question.id)!
    }

    options(question: SwpppQuestion) {
        return (question.options || '').split('|').filter(option => option.length > 0)
    }

    toggleChoice(question: SwpppQuestion, option: string) {
        const row = this.answer(question)
        const selected = new Set((row.value || '').split('|').filter(item => item))
        if (selected.has(option)) selected.delete(option)
        else selected.add(option)
        row.value = Array.from(selected).join('|')
    }

    chosen(question: SwpppQuestion, option: string) {
        return (this.answer(question).value || '').split('|').includes(option)
    }

    isProblem(question: SwpppQuestion) {
        if (question.answerType !== 'YesNoNA') return false
        const value = this.answer(question).value
        const number = Number((question.text.match(/^(\d+)/) || [])[1])
        if (!number) return false
        return number <= 6 ? value === 'No' : value === 'Yes'
    }

    addProblemAction(question: SwpppQuestion) {
        this.actionText = question.text
        this.addAction()
    }

    imageUrl(image: { id: string; extension: string }) {
        return './Resources/Images/SwpppInspection/' + this.detail?.inspection.id + '/' + image.id + image.extension
    }

    localDateTime() {
        const value = this.detail?.inspection.inspectionDateTime
        if (!value) return ''
        const date = new Date(value)
        const pad = (n: number) => String(n).padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    setDateTime(value: string) {
        if (this.detail) this.detail.inspection.inspectionDateTime = new Date(value).toISOString()
    }

    saveBody() {
        const inspection = this.detail!.inspection
        return {
            inspection,
            answers: Array.from(this.answers.values()).map(answer => ({
                ...answer,
                id: answer.id || undefined,
            })),
        }
    }

    saveDraft() {
        this.services.data.post<SwpppInspectionDetail>('/api/SwpppInspection/SaveDraft', this.saveBody()).subscribe({
            next: detail => {
                this.apply(detail)
                this.message = 'Draft saved.'
            },
            error: error => this.fail(error, 'The draft could not be saved.'),
        })
    }

    copyLast() {
        this.services.data.post<SwpppInspectionDetail>('/api/SwpppInspection/CopyLast?id=' + this.detail!.inspection.id, {}).subscribe({
            next: detail => this.apply(detail),
            error: error => this.fail(error, 'The last inspection could not be copied.'),
        })
    }

    sign() {
        const inspection = this.detail!.inspection
        this.services.data.post('/api/SwpppInspection/SaveDraft', this.saveBody()).subscribe({
            next: () => {
                this.services.data
                    .post<SwpppInspectionDetail>('/api/SwpppInspection/Sign', {
                        inspectionId: inspection.id,
                        signedName: this.signedName,
                        signerTitle: this.signerTitle,
                        signatureConfirmed: this.confirmed,
                        finishedOn: new Date().toISOString(),
                    })
                    .subscribe({
                        next: () => this.router.navigate(['/field-tools/swppp/site', inspection.siteId]),
                        error: error => this.fail(error, 'The inspection could not be signed.'),
                    })
            },
            error: error => this.fail(error, 'The inspection could not be signed.'),
        })
    }

    deleteInspection() {
        const inspection = this.detail!.inspection
        this.services.data.http.delete('/api/SwpppInspection/' + inspection.id).subscribe({
            next: () => this.router.navigate(['/field-tools/swppp/site', inspection.siteId]),
            error: error => this.fail(error, 'The inspection could not be deleted.'),
        })
    }

    onPhotos(event: Event) {
        const input = event.target as HTMLInputElement
        const files = Array.from(input.files ?? [])
        if (files.length === 0 || !this.detail) return
        const data = new FormData()
        data.append('id', this.detail.inspection.id)
        files.forEach((file, index) => data.append('myFile' + index, file, file.name))
        this.services.data.post('/api/SwpppInspection/UploadImages', data).subscribe({
            next: () => this.load(this.detail!.inspection.id),
            error: error => this.fail(error, 'The photos could not be uploaded.'),
        })
    }

    saveVisual() {
        const input = document.getElementById('visualFile') as HTMLInputElement
        const file = input?.files?.[0]
        if (!file || !this.detail) return
        if (this.visualNote.trim()) {
            const note = 'Visual: ' + this.visualNote.trim()
            this.detail.inspection.comments = this.detail.inspection.comments ? this.detail.inspection.comments + '\n' + note : note
        }
        const data = new FormData()
        data.append('id', this.detail.inspection.id)
        data.append('myFile0', file, file.name)
        this.services.data.post('/api/SwpppInspection/SaveDraft', this.saveBody()).subscribe({
            next: () => {
                this.services.data.post('/api/SwpppInspection/UploadImages', data).subscribe({
                    next: () => {
                        this.visualNote = ''
                        if (input) input.value = ''
                        this.load(this.detail!.inspection.id)
                    },
                    error: error => this.fail(error, 'The photo could not be uploaded.'),
                })
            },
            error: error => this.fail(error, 'The inspection could not be saved before the photo was added.'),
        })
    }

    focusAction() {
        this.actionText = this.actionText.trim()
        const box = this.actionBox?.nativeElement
        box?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        box?.focus()
    }

    onSiteMap(event: Event) {
        const input = event.target as HTMLInputElement
        const file = input.files?.[0]
        if (!file || !this.detail) return
        const title = file.name.replace(/\.[^.]+$/, '') || 'Site map'
        this.services.data.swpppDocuments
            .add({
                id: crypto.randomUUID(),
                siteId: this.detail.site.id,
                docType: 'SiteMap',
                title,
                reminderDaysBefore: 45,
                createdOn: new Date().toISOString(),
            } as SwpppDocument)
            .subscribe({
                next: saved => {
                    const data = new FormData()
                    data.append('id', saved.id)
                    data.append('myFile0', file, file.name)
                    this.services.data.post('/api/SwpppDocument/UploadImages', data).subscribe({
                        next: () => {
                            input.value = ''
                            const mapId = saved.id
                            this.services.data.get<SwpppInspectionDetail>('/api/SwpppInspection/GetDetail?id=' + this.detail!.inspection.id).subscribe({
                                next: detail => {
                                    this.apply(detail)
                                    if (this.detail) this.detail.inspection.siteMapDocumentId = mapId
                                },
                                error: error => this.fail(error, 'The site map was added, but the inspection could not be refreshed.'),
                            })
                        },
                        error: error => this.fail(error, 'The site map file could not be uploaded.'),
                    })
                },
                error: error => this.fail(error, 'The site map could not be added.'),
            })
    }

    addAction() {
        const description = this.actionText.trim()
        if (!this.detail || !description) {
            this.services.notification.error('Enter an action item before adding it.')
            return
        }
        this.services.data.swpppActionItems
            .add({
                id: crypto.randomUUID(),
                siteId: this.detail.site.id,
                inspectionId: this.detail.inspection.id,
                description,
                status: 'Open',
                createdOn: new Date().toISOString(),
            } as SwpppActionItem)
            .subscribe({
                next: () => {
                    this.actionText = ''
                    this.load(this.detail!.inspection.id)
                },
                error: error => this.fail(error, 'The action item could not be added.'),
            })
    }

    private fail(error: { error?: unknown }, fallback: string) {
        const body = error?.error
        let message = fallback
        if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) message = body
        else if (body && typeof body === 'object') {
            const problem = body as { detail?: string; title?: string }
            if (problem.detail || problem.title) message = problem.detail || problem.title || fallback
        }
        this.services.notification.error(message)
    }

    get recipientNames() {
        return (this.detail?.recipients ?? [])
            .map(person => person.name || person.email)
            .filter(name => !!name)
            .join(', ')
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsSwpppEdit])
    }

    get signed() {
        return this.detail?.inspection.status === 'Signed'
    }
}
